import { BUILTIN_SAMPLES } from './builtins';

export interface SampleInfo {
  id: string;
  name: string;
  buffer: AudioBuffer;
  rootNote: number;
}

/**
 * Session-scoped sample bank. AudioBuffers live here (they are not part of
 * the serializable project document — projects reference samples by id;
 * built-ins are regenerated on load, user imports live for the session).
 */
export class SampleBank {
  private samples = new Map<string, SampleInfo>();
  private builtinsLoaded = false;
  private listeners = new Set<() => void>();
  private version = 0;

  /** React integration: re-render subscribers when the bank changes. */
  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getVersion = (): number => this.version;
  private bump(): void {
    this.version++;
    for (const l of this.listeners) l();
  }

  /** Decode a user file into the bank and return its id. */
  async addFromBuffer(name: string, buffer: AudioBuffer, rootNote = 60): Promise<string> {
    const id = `sample-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    this.samples.set(id, { id, name, buffer, rootNote });
    this.bump();
    return id;
  }

  /** Re-add a sample under a specific id (zip import — keeps project refs valid). */
  restore(id: string, name: string, buffer: AudioBuffer, rootNote = 60): void {
    this.samples.set(id, { id, name, buffer, rootNote });
    this.bump();
  }

  get(id: string | null): SampleInfo | undefined {
    return id ? this.samples.get(id) : undefined;
  }

  list(): SampleInfo[] {
    return [...this.samples.values()];
  }

  remove(id: string): void {
    this.samples.delete(id);
    this.bump();
  }

  /** Generate the built-in kit into this context (idempotent). */
  ensureBuiltins(ctx: BaseAudioContext): void {
    if (this.builtinsLoaded) return;
    for (const b of BUILTIN_SAMPLES) {
      const data = b.make(ctx.sampleRate);
      const buf = ctx.createBuffer(1, data.length, ctx.sampleRate);
      buf.copyToChannel(data, 0);
      this.samples.set(b.id, { id: b.id, name: b.name, buffer: buf, rootNote: b.rootNote });
    }
    this.builtinsLoaded = true;
    this.bump();
  }
}

/** Process-wide singleton. */
export const sampleBank = new SampleBank();